import React from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  ONBOARDING_PATH,
  isOnboardingPageEnabled,
  isPaid,
  isPaymentAllowlisted,
} from "../../utils/paymentNav";

/**
 * Route guard that bounces unpaid users to /onboarding.
 *
 * Mounts as a layout route inside <IsAuthenticated>, so by the time this
 * runs we already know the user is signed in. Reads `paidStatus` from the
 * Redux auth slice (which mirrors the backend user document).
 *
 * Behaviour:
 *   - Feature flag off  → no-op pass-through (renders children).
 *   - Allow-listed path → pass-through (so /onboarding itself works).
 *   - paidStatus settled (paid|promo|missing-legacy) → pass-through.
 *   - Anything else → <Navigate to="/onboarding" replace />.
 */
const RequirePayment = () => {
  const location = useLocation();
  const userData = useSelector((s) => s?.auth?.userData);

  if (!isOnboardingPageEnabled()) return <Outlet />;
  if (isPaymentAllowlisted(location.pathname)) return <Outlet />;

  // userData may not be loaded yet right after refresh — let it through
  // for a tick rather than flicker to /onboarding. Once the profile
  // fetch resolves and this component re-renders we'll decide properly.
  if (!userData || typeof userData !== "object") return <Outlet />;

  if (isPaid(userData.paidStatus)) return <Outlet />;

  return <Navigate to={ONBOARDING_PATH} replace state={{ from: location }} />;
};

export default RequirePayment;
